/**
 * Small shared helpers for the City / Assets / Graph surfaces.
 *
 * Status/priority vocabularies are re-used from `src/lib/filters.js` so the
 * Board, List and these surfaces cannot drift; the colours resolve to the
 * `--st-*` tokens declared in `components/graph/graph.css`. Asset vocabularies
 * mirror `todo_api/models/enums.py`.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

import { request } from '../../api.js'
import { STATUSES, STATUS_LABELS } from '../../lib/filters.js'

export { STATUSES, STATUS_LABELS }

/** Statuses that count as closed (mirrors CLOSED_STATUSES server-side). */
export const CLOSED_STATUSES = new Set(['done', 'wont_do'])

/** Label + colour token per ticket status. Tokens live in graph.css. */
export const STATUS_META = Object.fromEntries(
  STATUSES.map((s) => [s, { label: STATUS_LABELS[s], color: `var(--st-${s})` }]),
)

/** Priorities p0..p3, coloured with the shared --p0..--p3 tokens. */
export const PRIORITY_META = {
  p0: { label: 'P0', color: 'var(--p0)' },
  p1: { label: 'P1', color: 'var(--p1)' },
  p2: { label: 'P2', color: 'var(--p2)' },
  p3: { label: 'P3', color: 'var(--p3)' },
}

/** Asset kinds and statuses (todo_api.models.enums.AssetKind / AssetStatus). */
export const ASSET_KINDS = ['vehicle', 'camera', 'compute', 'gps', 'router']
export const ASSET_STATUSES = ['in_service', 'staging', 'repair', 'retired']

export const ASSET_STATUS_META = {
  in_service: { label: 'In service', color: 'var(--ok)' },
  staging: { label: 'Staging', color: 'var(--accent)' },
  repair: { label: 'Repair', color: 'var(--warn)' },
  retired: { label: 'Retired', color: 'var(--text-faint)' },
}

/** Join class names, dropping falsy entries. */
export function cls(...parts) {
  return parts.filter(Boolean).join(' ')
}

/** Format an ISO date/datetime as `2026-08-20`, or an em dash when absent. */
export function fmtDate(value) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  return d.toISOString().slice(0, 10)
}

/** Format a drive window as `2026-08-01 → 2026-08-09`. */
export function fmtRange(start, end) {
  if (!start && !end) return '—'
  return `${fmtDate(start)} → ${fmtDate(end)}`
}

/**
 * Build a query string from a plain object; array values repeat the key,
 * which is what FastAPI's `Query(default=[])` parameters expect.
 */
export function qs(params) {
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(params || {})) {
    if (value === undefined || value === null || value === '') continue
    if (Array.isArray(value)) value.forEach((v) => sp.append(key, v))
    else sp.append(key, String(value))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

/**
 * Run an async loader and expose `{ data, error, loading, reload }`.
 *
 * `deps` behaves like a useEffect dependency array. Out-of-order responses are
 * discarded so fast filter changes cannot show stale data.
 */
export function useAsync(loader, deps = []) {
  const [state, setState] = useState({ data: null, error: null, loading: true })
  const seq = useRef(0)
  const run = useCallback(() => {
    const mine = ++seq.current
    setState((prev) => ({ ...prev, loading: true, error: null }))
    Promise.resolve()
      .then(loader)
      .then((data) => {
        if (seq.current === mine) setState({ data, error: null, loading: false })
      })
      .catch((error) => {
        if (seq.current === mine) setState({ data: null, error, loading: false })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  useEffect(() => {
    run()
  }, [run])
  return { ...state, reload: run }
}

/**
 * GET a path below `/api/v1` using the shared fetch wrapper in `src/api.js`.
 *
 * `api.js` exposes named endpoint helpers rather than a generic getter, and
 * these three surfaces need endpoints it does not wrap yet (city summary,
 * asset tickets, graph), so they call `request` directly — same auth, same
 * `ApiError` normalisation.
 */
export function apiGet(path) {
  return request(path)
}
